import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { observer } from "mobx-react-lite";
import { CommandAction, Vec2, Webgl2New } from "src/image-editor-core";
import type { MeasurementComponent } from "src/image-editor-core/utils/graphic/webgl2/webgl2-component/components/measurements-component/measurement-component";
import {
  Button,
  NotificationText,
  NotificationTextSecondary,
  useDidMount,
  useDidUpdate,
  isEqual,
  Typography,
  Switch,
} from "@gemlightbox/core-kit";

import { useStores } from "src/hooks";
import { focusAndScrollToCenter, unsavedChangesNotification } from "src/utils";
import { editMediaStore } from "../../edit-media.store";
import { GroupBlock } from "../group-block";
import { MeasurementItem } from "./measurement-item";
import { FANTOM_MEASUREMENT_CLASS, measurementsLimit } from "./measurement.constants";
import { mapMeasurementsToPlain } from "./measurement.utils";

import styles from "./measurements.module.css";

const MEASUREMENT_HANDLE_CAPTURE_RADIUS = 48;
const MEASUREMENT_LINE_CAPTURE_DISTANCE = 28;
const MEASUREMENT_MIN_VISIBLE_LENGTH = 60;
const MEASUREMENT_CURSOR_HINT_STORAGE_KEY = "edit-media.measurements.cursor-hint-dismissed";
// Fine-tune overlay DOM input baseline (in WebGL px, will be scaled to CSS px).
// Positive value moves DOM text/caret DOWN relative to the WebGL label box.
const CANVAS_MEASUREMENT_INPUT_BASELINE_OFFSET_PX = 1;

type MeasurementDragMode = "idle" | "drawing" | "drag-start" | "drag-end" | "drag-line";

type MeasurementDragState = {
  mode: MeasurementDragMode;
  measurement: Webgl2New.MeasurementComponent | null;
  prevMeasurement?: Webgl2New.MeasurementComponent | null;
  pointerStart?: Vec2;
  startPos?: Vec2;
  endPos?: Vec2;
};

type CanvasMeasurementEditState = {
  measurement: MeasurementComponent;
  initialText: string;
};

const isPointNear = (point: Vec2, target: Vec2, radius = MEASUREMENT_HANDLE_CAPTURE_RADIUS) => {
  return point.distanceTo(target) <= radius;
};

const getDistanceToSegment = (point: Vec2, start: Vec2, end: Vec2) => {
  const x1 = start.x;
  const y1 = start.y;
  const x2 = end.x;
  const y2 = end.y;
  const px = point.x;
  const py = point.y;

  const dx = x2 - x1;
  const dy = y2 - y1;

  if (!dx && !dy) {
    return Math.hypot(px - x1, py - y1);
  }

  const t = ((px - x1) * dx + (py - y1) * dy) / (dx * dx + dy * dy);
  const clampedT = Math.max(0, Math.min(1, t));
  const projectionX = x1 + clampedT * dx;
  const projectionY = y1 + clampedT * dy;

  return Math.hypot(px - projectionX, py - projectionY);
};

const isPointNearSegment = (
  point: Vec2,
  start: Vec2,
  end: Vec2,
  threshold = MEASUREMENT_LINE_CAPTURE_DISTANCE,
) => {
  return getDistanceToSegment(point, start, end) <= threshold;
};

export const Measurements: React.FC = observer(() => {
  const { localeStore, notificationStore } = useStores();

  const { renderer, measurementsComponent, fantomMeasurement } = editMediaStore;

  const [measurements, setMeasurements] = useState<MeasurementComponent[]>(() => {
    return editMediaStore.measurementsArr.map((currentMeasure) => currentMeasure.clone());
  });
  const [measurementTexts, setMeasurementTexts] = useState(() => {
    return measurements.map((measurement) => measurement.text);
  });
  const [tempMeasurements] = useState(() => mapMeasurementsToPlain(measurements));

  const measurementsLength = measurements.length;
  const fantomMeasurementIndex = measurementsLength;

  const hasReachedLimitRef = useRef(false);
  hasReachedLimitRef.current = measurementsLength >= measurementsLimit;

  const hasDifference = !isEqual(tempMeasurements, mapMeasurementsToPlain(measurements));
  const hasEmptyMeasurementValue = measurements.some((measurement) => {
    return measurement.isValidPos && !measurement.text.trim();
  });

  const [canvasMeasurementEdit, setCanvasMeasurementEdit] =
    useState<CanvasMeasurementEditState | null>(null);
  const [canvasMeasurementInputValue, setCanvasMeasurementInputValue] = useState("");
  const canvasMeasurementInputWrapperRef = useRef<HTMLDivElement | null>(null);
  const canvasMeasurementInputRef = useRef<HTMLInputElement | null>(null);

  const measurementsRef = useRef<MeasurementComponent[]>(measurements);
  useEffect(() => {
    measurementsRef.current = measurements;
  }, [measurements]);

  const getMeasurementText = useCallback(
    (measurement: MeasurementComponent) => {
      const index = measurementsRef.current.findIndex((current) => current === measurement);
      return index === -1 ? measurement.text : measurementTexts[index] ?? measurement.text;
    },
    [measurementTexts],
  );

  const handleMeasurementTextChange = useCallback(
    (measurement: MeasurementComponent, value: string) => {
      measurement.changeText(value);
      setMeasurementTexts((prevState) => {
        const index = measurementsRef.current.findIndex((current) => current === measurement);
        if (index === -1) return prevState;

        const nextState = prevState.slice();
        nextState[index] = value;
        return nextState;
      });

      if (canvasMeasurementEdit?.measurement === measurement) {
        setCanvasMeasurementInputValue(value);
      }

      renderer.render();
    },
    [canvasMeasurementEdit, renderer],
  );

  const syncCanvasMeasurementCaret = useCallback(() => {
    const input = canvasMeasurementInputRef.current;
    if (!input || !canvasMeasurementEdit) return;

    canvasMeasurementEdit.measurement.textComponent.setCaretIndex(input.selectionStart ?? 0);
    renderer.render();
  }, [canvasMeasurementEdit, renderer]);

  const cursorHintDismissedRef = useRef(false);

  const [isCursorHintVisible, setCursorHintVisible] = useState(() => {
    if (typeof window === "undefined") return true;
    const isDismissed = window.localStorage.getItem(MEASUREMENT_CURSOR_HINT_STORAGE_KEY) === "true";
    cursorHintDismissedRef.current = isDismissed;
    return !isDismissed;
  });
  const [isCursorInsideCanvas, setCursorInsideCanvas] = useState(false);
  const [cursorHintPosition, setCursorHintPosition] = useState<{ x: number; y: number } | null>(
    null,
  );

  const dismissCursorHint = useCallback(() => {
    if (cursorHintDismissedRef.current) return;
    cursorHintDismissedRef.current = true;
    setCursorHintVisible(false);
    setCursorInsideCanvas(false);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(MEASUREMENT_CURSOR_HINT_STORAGE_KEY, "true");
    }
  }, []);

  useLayoutEffect(() => {
    if (!renderer?.canvas) return;

    const canvas = renderer.canvas;

    const handleMouseEnter = () => {
      if (cursorHintDismissedRef.current) return;
      setCursorInsideCanvas(true);
    };

    const handleMouseLeave = () => {
      setCursorInsideCanvas(false);
      setCursorHintPosition(null);
    };

    const handleMouseMove = (event: MouseEvent) => {
      if (cursorHintDismissedRef.current) return;
      setCursorHintPosition({
        x: event.clientX,
        y: event.clientY,
      });
    };

    canvas.addEventListener("mouseenter", handleMouseEnter);
    canvas.addEventListener("mouseleave", handleMouseLeave);
    canvas.addEventListener("mousemove", handleMouseMove);

    return () => {
      canvas.removeEventListener("mouseenter", handleMouseEnter);
      canvas.removeEventListener("mouseleave", handleMouseLeave);
      canvas.removeEventListener("mousemove", handleMouseMove);
    };
  }, [renderer]);

  const updateCanvasMeasurementInputPosition = useCallback(() => {
    if (typeof window === "undefined") return;
    if (!renderer?.canvas) return;
    if (!canvasMeasurementEdit) return;

    const wrapper = canvasMeasurementInputWrapperRef.current;
    if (!wrapper) return;

    const measurement = canvasMeasurementEdit.measurement;
    if (!measurement.isValidPos) return;

    const center = measurement.startPos.median(measurement.endPos);
    const dpr = window.devicePixelRatio || 1;
    const topLeftDevicePx = renderer.camera.convertGlobalToTopLeft(center);
    const topLeftCssPx = topLeftDevicePx.divide(dpr);

    const canvasRect = renderer.canvas.getBoundingClientRect();

    const textSize = measurement.textComponent.transform.size;
    const widthCss = Math.max((Math.abs(textSize.width) * renderer.camera.scale) / dpr, 2);
    const heightCss = Math.max((Math.abs(textSize.height) * renderer.camera.scale) / dpr, 2);

    // NOTE: align textarea exactly to measurement label center
    let left = canvasRect.left + topLeftCssPx.x;
    let top = canvasRect.top + topLeftCssPx.y;

    const padding = 12;
    const halfW = widthCss / 2;
    const halfH = heightCss / 2;
    left = Math.min(Math.max(left, padding + halfW), window.innerWidth - padding - halfW);
    top = Math.min(Math.max(top, padding + halfH), window.innerHeight - padding - halfH);

    const degrees =
      (measurement.transform.degrees + measurement.textComponent.transform.degrees + 360) % 360;

    wrapper.style.left = `${left}px`;
    wrapper.style.top = `${top}px`;
    wrapper.style.width = `${widthCss}px`;
    wrapper.style.height = `${heightCss}px`;
    wrapper.style.transform = `translate(-50%, -50%) rotate(${degrees}deg)`;

    const input = canvasMeasurementInputRef.current;
    if (input) {
      const pxToCss = renderer.camera.scale / dpr;
      const fontSizeCss = measurement.textComponent.fontSize * pxToCss;
      const paddingCss = 5 * pxToCss;
      const baselineOffsetCss = CANVAS_MEASUREMENT_INPUT_BASELINE_OFFSET_PX * pxToCss;
      const paddingTop = paddingCss + baselineOffsetCss;
      const paddingBottom = Math.max(paddingCss - baselineOffsetCss, 0);

      // Keep DOM metrics in sync with the WebGL text texture (which uses +10px padding in its own canvas)
      input.style.font = `normal 400 ${fontSizeCss}px/${fontSizeCss}px 'Inter'`;
      input.style.padding = `${paddingTop}px ${paddingCss}px ${paddingBottom}px ${paddingCss}px`;
    }
  }, [canvasMeasurementEdit, renderer]);

  useLayoutEffect(() => {
    if (!canvasMeasurementEdit) return;
    if (!renderer) return;

    updateCanvasMeasurementInputPosition();
    const dispose = renderer.events.on("render", updateCanvasMeasurementInputPosition);
    return () => dispose();
  }, [canvasMeasurementEdit, renderer, updateCanvasMeasurementInputPosition]);

  useLayoutEffect(() => {
    if (!canvasMeasurementEdit) return;
    if (typeof window === "undefined") return;

    window.requestAnimationFrame(() => {
      const input = canvasMeasurementInputRef.current;
      if (!input) return;
      input.focus();
      const len = input.value?.length ?? 0;
      input.setSelectionRange?.(len, len);
    });
  }, [canvasMeasurementEdit]);

  useLayoutEffect(() => {
    if (!renderer?.canvas) return;

    const handleDblClick = (event: MouseEvent) => {
      const list = measurementsRef.current;
      if (!list.length) return;

      const mousePos = renderer.camera.getGlobalMousePixelPosition(event);
      const scale = renderer.camera.scale || 1;
      const maxDistance = MEASUREMENT_LINE_CAPTURE_DISTANCE / scale;

      let nearestMeasurement: MeasurementComponent | null = null;
      let nearestDistance = Number.POSITIVE_INFINITY;
      let nearestText = "";

      for (const measurement of list) {
        if (!measurement.isValidPos) continue;
        const distance = getDistanceToSegment(mousePos, measurement.startPos, measurement.endPos);
        if (distance > maxDistance) continue;

        if (!nearestMeasurement || distance < nearestDistance) {
          nearestMeasurement = measurement;
          nearestDistance = distance;
          nearestText = getMeasurementText(measurement);
        }
      }

      if (!nearestMeasurement) return;

      event.preventDefault();

      // enter canvas text edit mode (text is rendered in WebGL, textarea is hidden and only used for IME/keyboard)
      nearestMeasurement.textComponent.setEditing(true);
      nearestMeasurement.changeText(nearestText);

      setCanvasMeasurementInputValue(nearestText);
      setCanvasMeasurementEdit({ measurement: nearestMeasurement, initialText: nearestText });
      editMediaStore.currentMeasurement = nearestMeasurement;
      renderer.render();
    };

    renderer.canvas.addEventListener("dblclick", handleDblClick);
    return () => {
      renderer.canvas.removeEventListener("dblclick", handleDblClick);
    };
  }, [getMeasurementText, renderer]);

  const handleStraightenModeChange = (value: boolean) => {
    editMediaStore.setStraightenMode(value);
    measurements.forEach((measurement) => {
      measurement.setMeasurementsStraightenMode(value);
    });
  };

  const handleRemoveMeasurement = (measurement: Webgl2New.MeasurementComponent) => {
    if (canvasMeasurementEdit?.measurement === measurement) {
      measurement.textComponent.setEditing(false);
      setCanvasMeasurementEdit(null);
    }
    if (measurement === editMediaStore.currentMeasurement) {
      editMediaStore.currentMeasurement = editMediaStore.fantomMeasurement;
    }

    setMeasurements((prevState) => {
      return prevState.filter((prev) => prev !== measurement);
    });
    setMeasurementTexts((prevState) => {
      const index = measurementsRef.current.findIndex((prev) => prev === measurement);
      if (index === -1) return prevState;

      return prevState.filter((_, textIndex) => textIndex !== index);
    });
  };

  const handleAddMeasurement = (measurement: Webgl2New.MeasurementComponent) => {
    if (!measurement.isValidPos) return;
    setMeasurements((prevState) => prevState.concat(measurement));
    setMeasurementTexts((prevState) => prevState.concat(measurement.text));

    const newFantomMeasurement = new Webgl2New.MeasurementComponent({
      color: measurement.color,
      colorIndex: measurement.colorIndex,
      straightenMode: editMediaStore.measurementsStraightenMode,
    });

    editMediaStore.currentMeasurement = measurement;
    editMediaStore.fantomMeasurement = newFantomMeasurement;
  };

  const handleCancel = () => {
    editMediaStore.unregisterTabBlockCallback();
    editMediaStore.measurementsComponent.setChildren(editMediaStore.measurementsArr);
    editMediaStore.closeMeasurementsTab();
  };

  const handleConfirmCancel = () => {
    if (hasDifference) return unsavedChangesNotification(handleCancel);
    handleCancel();
  };

  const handleDone = () => {
    if (hasEmptyMeasurementValue) return;
    editMediaStore.unregisterTabBlockCallback();

    const actionMeasurements = measurements;

    const undoMeasurements = editMediaStore.measurementsArr;

    editMediaStore.closeMeasurementsTab();

    renderer.commands.execute(
      new CommandAction(
        "Measurements change",
        () => {
          editMediaStore.measurementsArr = actionMeasurements;
          editMediaStore.measurementsComponent.setChildren(actionMeasurements);
          renderer.render();
        },
        () => {
          editMediaStore.measurementsArr = undoMeasurements;
          editMediaStore.measurementsComponent.setChildren(undoMeasurements);
          renderer.render();
        },
      ),
    );
  };

  const handleConfirmDone = () => {
    if (hasEmptyMeasurementValue) return;
    if (fantomMeasurement.isValidPos) {
      notificationStore.open({
        title: localeStore.t('["edit-media"].inspector.measurements["confirm-done"].title'),
        message: (
          <>
            <NotificationTextSecondary>
              {localeStore.t('["edit-media"].inspector.measurements["confirm-done"].message.main')}
            </NotificationTextSecondary>
            <NotificationText>
              {localeStore.t(
                '["edit-media"].inspector.measurements["confirm-done"].message.strong',
              )}
            </NotificationText>
          </>
        ),
        confirmText: localeStore.t("common.buttons.done"),
        cancelText: localeStore.t("common.buttons.cancel"),
        onOk: handleDone,
      });

      return;
    }

    handleDone();
  };

  useLayoutEffect(() => {
    const prevDisplay = measurementsComponent.style.display;
    measurementsComponent.style.display = "initial";
    return () => {
      measurementsComponent.style.display = prevDisplay;
    };
  }, []);

  useLayoutEffect(() => {
    editMediaStore.measurementsComponent.setChildren(measurements.concat(fantomMeasurement));
    renderer.render();
  }, [measurementsLength]);

  useDidUpdate(
    () => {
      return editMediaStore.registerTabBlockCallback(({ unregister, proceed }) => {
        if (hasDifference) {
          unsavedChangesNotification(() => {
            unregister();
            handleCancel();
            proceed();
          });
          return true;
        }

        handleCancel();
      });
    },
    [hasDifference],
    true,
  );

  // NOTE: fix bug when you close measurements but still can 'select' measurement components
  useDidMount(() => {
    measurementsComponent.style.pointerEvents = "initial";
    return () => {
      measurementsComponent.style.pointerEvents = "none";
    };
  });

  const measurementDragStateRef = useRef<MeasurementDragState>({
    mode: "idle",
    measurement: null,
  });

  useDidMount(() => {
    const isFantomSelectedCheck = () => {
      return editMediaStore.currentMeasurement === editMediaStore.fantomMeasurement;
    };

    const disposeDown = renderer.tools.measurementTool.events.on("onDown", (e) => {
      dismissCursorHint();
      const mouseDownPosition = renderer.camera.getGlobalMousePixelPosition(e);
      const currentMeasurement = editMediaStore.currentMeasurement;
      if (!currentMeasurement) return;

      const measurementHasLine = currentMeasurement.isValidPos;
      const isFantomSelected = isFantomSelectedCheck();
      const startPos = measurementHasLine ? currentMeasurement.startPos.clone() : null;
      const endPos = measurementHasLine ? currentMeasurement.endPos.clone() : null;

      const isNearStart = Boolean(
        measurementHasLine && startPos && isPointNear(mouseDownPosition, startPos),
      );
      const isNearEnd = Boolean(
        measurementHasLine && endPos && isPointNear(mouseDownPosition, endPos),
      );
      const isNearLine = Boolean(
        measurementHasLine &&
          startPos &&
          endPos &&
          isPointNearSegment(mouseDownPosition, startPos, endPos),
      );

      if (measurementHasLine) {
        if (isNearStart || isNearEnd || isNearLine) {
          measurementDragStateRef.current = {
            mode: isNearStart ? "drag-start" : isNearEnd ? "drag-end" : "drag-line",
            measurement: currentMeasurement,
            pointerStart: mouseDownPosition.clone(),
            startPos: startPos ?? undefined,
            endPos: endPos ?? undefined,
          };
          return;
        }

        if (hasReachedLimitRef.current) {
          measurementDragStateRef.current = { mode: "idle", measurement: null };
          return;
        }

        const fantomMeasurement = editMediaStore.fantomMeasurement;
        editMediaStore.currentMeasurement = fantomMeasurement;
        measurementDragStateRef.current = {
          mode: "drawing",
          measurement: fantomMeasurement,
          prevMeasurement: currentMeasurement,
        };

        fantomMeasurement.changePositions(mouseDownPosition, mouseDownPosition);
        renderer.render();
        return;
      }

      if (!isFantomSelected) {
        measurementDragStateRef.current = { mode: "idle", measurement: null };
        return;
      }

      if (hasReachedLimitRef.current) {
        measurementDragStateRef.current = { mode: "idle", measurement: null };
        return;
      }

      measurementDragStateRef.current = {
        mode: "drawing",
        measurement: currentMeasurement,
      };

      currentMeasurement.changePositions(mouseDownPosition, mouseDownPosition);
      renderer.render();
    });

    const disposeMove = renderer.tools.measurementTool.events.on("onMove", (e) => {
      const dragState = measurementDragStateRef.current;
      if (dragState.mode === "idle" || !dragState.measurement) return;

      const movePosition = renderer.camera.getGlobalMousePixelPosition(e);
      switch (dragState.mode) {
        case "drawing":
          if (dragState.measurement !== editMediaStore.fantomMeasurement) return;
          dragState.measurement.changeEndPosition(movePosition);
          renderer.render();
          break;
        case "drag-start":
          dragState.measurement.changeStartPosition(movePosition);
          renderer.render();
          break;
        case "drag-end":
          dragState.measurement.changeEndPosition(movePosition);
          renderer.render();
          break;
        case "drag-line":
          if (!dragState.pointerStart || !dragState.startPos || !dragState.endPos) return;
          const shift = movePosition.clone().sub(dragState.pointerStart);
          const nextStart = dragState.startPos.clone().add(shift);
          const nextEnd = dragState.endPos.clone().add(shift);
          dragState.measurement.changePositions(nextStart, nextEnd);
          renderer.render();
          break;
        default:
          break;
      }
    });

    const disposeUp = renderer.tools.measurementTool.events.on("onUp", () => {
      const { measurement, prevMeasurement } = measurementDragStateRef.current;
      measurementDragStateRef.current = { mode: "idle", measurement: null };

      if (!measurement) return;

      const isFantomMeasurement = measurement === editMediaStore.fantomMeasurement;

      // NOTE: user just clicked the canvas (start == end) -> invalid drawing, don't focus input
      if (isFantomMeasurement && !measurement.isValidPos) {
        measurement.changePositions(Vec2.zero(), Vec2.zero());
        if (prevMeasurement && prevMeasurement !== measurement) {
          editMediaStore.currentMeasurement = prevMeasurement;
        }
        renderer.render();
        return;
      }

      if (isFantomMeasurement && measurement.isValidPos) {
        const measurementLength = measurement.startPos.distanceTo(measurement.endPos);
        if (measurementLength < MEASUREMENT_MIN_VISIBLE_LENGTH) return;
        handleAddMeasurement(measurement);
        return;
      }

      if (!isFantomMeasurement) return;

      const measureItemContainer = document.querySelector("." + FANTOM_MEASUREMENT_CLASS);
      if (!measureItemContainer) return;
      const measureInput = measureItemContainer.querySelector("input");
      if (!measureInput) return;
      focusAndScrollToCenter(measureInput, { behavior: "smooth" });
    });

    const disposeSelect = renderer.tools.measurementTool.events.on("onSelect", (component) => {
      editMediaStore.currentMeasurement = component;
    });

    return () => {
      disposeDown();
      disposeMove();
      disposeUp();
      disposeSelect();
    };
  });

  const measurementCursorHint =
    typeof document !== "undefined" &&
    isCursorHintVisible &&
    isCursorInsideCanvas &&
    cursorHintPosition
      ? createPortal(
          <div
            className={styles.measurementCursorHint}
            style={{
              transform: `translate3d(${cursorHintPosition.x + 12}px, ${
                cursorHintPosition.y + 12
              }px, 0)`,
            }}
          >
            {localeStore.t('["edit-media"]["measurement-tutorial"]["ol-1"]')}
          </div>,
          document.body,
        )
      : null;

  const canvasMeasurementHiddenInput =
    typeof document !== "undefined" && canvasMeasurementEdit
      ? createPortal(
          <div ref={canvasMeasurementInputWrapperRef} className={styles.canvasMeasurementInput}>
            <input
              ref={canvasMeasurementInputRef}
              className={styles.canvasMeasurementHiddenInput}
              type="text"
              value={canvasMeasurementInputValue}
              maxLength={50}
              onChange={(e) => {
                const value = e.currentTarget.value;
                handleMeasurementTextChange(canvasMeasurementEdit.measurement, value);
                canvasMeasurementEdit.measurement.textComponent.setCaretIndex(
                  e.currentTarget.selectionStart ?? value.length,
                );
                renderer.render();
              }}
              onSelect={syncCanvasMeasurementCaret}
              onKeyUp={syncCanvasMeasurementCaret}
              onClick={syncCanvasMeasurementCaret}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  canvasMeasurementEdit.measurement.textComponent.setEditing(false);
                  setCanvasMeasurementEdit(null);
                  return;
                }

                if (e.key === "Escape") {
                  handleMeasurementTextChange(
                    canvasMeasurementEdit.measurement,
                    canvasMeasurementEdit.initialText,
                  );
                  canvasMeasurementEdit.measurement.textComponent.setEditing(false);
                  renderer.render();
                  setCanvasMeasurementEdit(null);
                }
              }}
              onBlur={() => {
                canvasMeasurementEdit.measurement.textComponent.setEditing(false);
                setCanvasMeasurementEdit(null);
                renderer.render();
              }}
            />
          </div>,
          document.body,
        )
      : null;

  return (
    <>
      <GroupBlock className={styles.straighteningBlockGroup}>
        <Typography size="small600" color="textSecondary">
          {localeStore.t('["edit-media"].inspector.measurements["auto-straightening-title"]')}
        </Typography>
        <div className={styles.switchContainer}>
          <Switch
            onChange={handleStraightenModeChange}
            checked={editMediaStore.measurementsStraightenMode}
          />
          <Typography size="small" color="textSecondary">
            {localeStore.t('["edit-media"].inspector.measurements["auto-straightening-check"]')}
          </Typography>
        </div>
      </GroupBlock>
      {measurements.map((measurement, i) => (
        <MeasurementItem
          key={i}
          index={i}
          measurement={measurement}
          textValue={measurementTexts[i] ?? measurement.text}
          onRemoveMeasurement={handleRemoveMeasurement}
          onMeasurementChange={handleMeasurementTextChange}
          isReal
          disableAutoFocus={canvasMeasurementEdit?.measurement === measurement}
        />
      ))}
      {!hasReachedLimitRef.current && (
        <MeasurementItem
          className={FANTOM_MEASUREMENT_CLASS}
          index={fantomMeasurementIndex}
          measurement={fantomMeasurement}
        />
      )}
      <div className={styles.buttons}>
        <Button appearance="tertiaryOutlined" onClick={handleConfirmCancel}>
          {localeStore.t("common.buttons.cancel")}
        </Button>
        <Button onClick={handleConfirmDone} disabled={!hasDifference || hasEmptyMeasurementValue}>
          {localeStore.t("common.buttons.done")}
        </Button>
      </div>
      {canvasMeasurementHiddenInput}
      {measurementCursorHint}
    </>
  );
});

export default Measurements;
